import React, { useContext, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { LicenseManager } from "ag-grid-enterprise";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import {
    agDefaultSidebar,
    agDefaultColDef,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    mainContext,
} from "@ts/constants";
import {
    sortByStatusAndColumn,
    agFilterModified,
    onModelUpdated,
    onRowDataChanged,
    isExternalFilterPresent,
    doesExternalFilterPass,
    onFilterChanged,
    showCountOfDisplayedData,
    initialFilters,
    resetActiveGridState,
    getActiveModule,
    autoSizeAll,
} from "@ts/utils";
import { ruleLinkFilters } from "@ts/filters";
import { applyCheckedFilter } from "@ts/performance-overview";
import { setGlobalGridApi } from "@features/gridApiSlice";
import {
    GridLayoutTypes,
    IChannel,
    IMainContext,
    ParentModule,
    RevinitiEnv,
    SubModule,
    TActiveClientId,
} from "@ts/common-types";
import { Dispatch } from "redux";

const AgGridLayout: React.FC<GridLayoutTypes> = React.memo(
    ({
        columnDefs,
        rowData,
        setGridApi,
        onSelectionChanged,
        updateModule,
        loading,
        pagination,
        isTotalRowsReq,
        isRowSelectable,
        isSortByStatusReq,
        rowHeight,
        paginationPageSize,
        serverSideDatasource,
        rowModelType,
        serverSideStoreType,
        loadingCellRenderer,
        rowDragManaged,
        onRowDragEnd,
        cacheBlockSize,
        maxBlocksInCache,
        isServerSideGrid = false,
        getRowStyle

    }) => {
        process.env.VITE_NODE_ENV === RevinitiEnv.PROD &&
            LicenseManager.setLicenseKey(process.env.VITE_AGGRID_LICENSE_KEY);
        const { setIsCheckboxSelected } = useContext<IMainContext>(mainContext);
        const dispatch = useDispatch<Dispatch>();
        const [api, setApi] = useState<GridApi>();
        const ref = useRef(null);
        const activeClientId = useSelector(
            (state: TActiveClientId) => state.activeClientId.value
        );
        const channel = useSelector((state: IChannel) => state.channel.value);
        const { activeModule } = getActiveModule();
        const resetCheckboxOfModuleWithTabsList = [
            ParentModule.PERFORMANCE_OVERVIEW,
            SubModule.SUGGESTED_NEG_KWS,
            ParentModule.KEYWORD_RECOMMENDATIONS,
            ParentModule.CAMPAIGN_RECOMMENDATIONS,
        ];

        const onGridReady = () => {
            if (updateModule) {
                updateModule();
            }

            if (resetCheckboxOfModuleWithTabsList.includes(activeModule)) {
                setIsCheckboxSelected(false);
            }

            dispatch(setGlobalGridApi(ref.current.api));
            setGridApi(ref.current.api);
            initialFilters(ref.current.api);
            setApi(ref.current.api);
            applyCheckedFilter(ref.current.api);
        };

        const onFilterModified = (event: Record<string, any>) =>
            agFilterModified(event);

        const onFirstDataRendered = (e: GridReadyEvent) => {
            ruleLinkFilters(ref.current);
            isSortByStatusReq && sortByStatusAndColumn(e.columnApi);
        };

        const onSelectionChangedHandler = (event: GridReadyEvent) => {
            setIsCheckboxSelected(!!event.api.getSelectedNodes().length);
            onSelectionChanged && onSelectionChanged(event);
        };

        // For setting grid loaders and grid filter count badges
        useEffect(() => {
            if (api) {
                if (loading) {
                    api.showLoadingOverlay();
                } else {
                    if (rowData && rowData.length === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                        if (isTotalRowsReq) {
                            onModelUpdated({ api });
                        }
                    }
                    autoSizeAll(true, ref);
                }
                showCountOfDisplayedData(api);
            }
        }, [loading, rowData, api]);

        // For resetting current grid state on client and channel change
        // Setting initial default filters on client and channel change
        useEffect(() => {
            if (ref.current.api && !isServerSideGrid) {
                resetActiveGridState(ref.current.api);
                initialFilters(ref.current.api);
            }
        }, [activeClientId, channel]);

        const gridProps = {
            ref,
            onGridReady,
            isTotalRowsReq,
            rowData,
            columnDefs,
            overlayLoadingTemplate,
            overlayNoRowsTemplate,
            onFilterModified,
            onFilterChanged,
            onFirstDataRendered,
            pagination,
            onRowDataChanged,
            isExternalFilterPresent,
            doesExternalFilterPass,
            isRowSelectable,
            rowHeight,
            paginationPageSize,
            serverSideDatasource,
            rowModelType,
            serverSideStoreType,
            loadingCellRenderer,
            rowDragManaged,
            onRowDragEnd,
            cacheBlockSize,
            maxBlocksInCache,
            getRowStyle
        };

        return (
            <>
                <AgGridReact
                    {...gridProps}
                    sideBar={agDefaultSidebar}
                    defaultColDef={agDefaultColDef}
                    animateRows={true}
                    suppressRowClickSelection={true}
                    suppressScrollOnNewData={true}
                    onSelectionChanged={onSelectionChangedHandler}
                    rowSelection="multiple"
                ></AgGridReact>
            </>
        );
    }
);

export default AgGridLayout;
